import type { TimeToSubmit } from '@compass/client/types/cases_analytics';
import React, { useMemo, useState } from 'react';

import { ChartCard } from '@/pages/analytics/components/ChartCard';
import { ChartRenderBoundary } from '@/pages/analytics/components/ChartRenderBoundary';
import { ChartBodySkeleton } from '@/pages/analytics/components/ChartStates';
import { ScopeToggle } from '@/pages/analytics/components/ScopeToggle';
import { StackedBarChart } from '@/pages/analytics/components/StackedBarChart';
import type { useDownloadChart } from '@/pages/analytics/hooks/useDownloadChart';
import { normalizeTimeToSubmit } from '@/pages/analytics/utils/normalizeChartRows';

import { HOUR_BUCKET_STARTS } from '../utils/constants';
import { type Grouping, GROUPING_OPTIONS } from '../utils/groupings';
import { hourBucketLabel } from '../utils/hourBuckets';

interface TimeToSubmitCardProps {
  data?: TimeToSubmit;
  colors: Map<string, string>;
  download: ReturnType<typeof useDownloadChart>;
  loading?: boolean;
}

const HEIGHT = 280;
const BUCKET_KEYS = HOUR_BUCKET_STARTS.map(String);
const bucketLabel = (x: string): string => hourBucketLabel(Number(x), HOUR_BUCKET_STARTS);

export const TimeToSubmitCard: React.FC<TimeToSubmitCardProps> = ({
  data,
  colors,
  download,
  loading,
}) => {
  const [grouping, setGrouping] = useState<Grouping>('by_report_type');
  const points = data?.[grouping];
  const rows = useMemo(
    () =>
      (points ?? []).map(p => ({
        x: String(p.bucket_start_hours ?? 0),
        group: p.group ?? '',
        group_name: p.group_name,
        value: p.case_count ?? 0,
      })),
    [points],
  );

  return (
    <ChartCard
      title="How long cases took to submit"
      subtitle="Submitted cases grouped by business hours from call to submission · 24h = SLA deadline"
      rightSlot={<ScopeToggle value={grouping} onChange={setGrouping} options={GROUPING_OPTIONS} />}
      onDownload={(format, exportElement) =>
        data &&
        download(
          normalizeTimeToSubmit(points ?? [], grouping),
          format,
          'How long cases took to submit',
          exportElement,
        )
      }
      downloadDisabled={!data}
    >
      {loading ? (
        <ChartBodySkeleton height={HEIGHT} />
      ) : (
        <ChartRenderBoundary resetKeys={[points, grouping]} height={HEIGHT}>
          <StackedBarChart
            rows={rows}
            xValues={BUCKET_KEYS}
            xFormatter={bucketLabel}
            valueFormatter={v => `${v.toLocaleString('en-US')} cases`}
            colorByKey={colors}
            ariaLabelPrefix="Time to submit bar chart"
            height={HEIGHT}
          />
        </ChartRenderBoundary>
      )}
    </ChartCard>
  );
};
